/**
 * feedback_manage_controller.js — feedback management page actions (Stimulus).
 *
 * Ports the inline <script> from templates/feedback/manage.html to an
 * element-scoped controller (data-controller="feedback-manage"). The inline
 * version defined global functions wired by inline onclick=/call-fn and, on a
 * DOMContentLoaded, registered a document-level show.bs.dropdown listener on
 * every visit — a leak that accumulated on the persistent document across Turbo
 * navigations. connect()/disconnect() scope the dropdown z-index fix to this
 * element and bind it once per page.
 *
 * Card actions read their target from data-* on the clicked element rather than
 * positional args baked into onclick=.
 *
 * Data attributes (on the controller element):
 *   data-manage-url — base URL for filter navigation.
 * Per-action data attributes (on buttons/links):
 *   data-feedback-id, data-status, data-priority,
 *   data-feedback-title, data-feedback-description, data-feedback-type.
 */

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
        connect() {
            this.manageUrl = this.element.dataset.manageUrl || window.location.pathname;
            this._highlightTargeted();

            // Lift the card holding an open dropdown above its siblings so the
            // menu isn't clipped by the next card. Events bubble from Bootstrap's
            // dropdown, so we can scope the listener to this element and reset on
            // close — no document-level leak.
            this._onDropdownShow = (event) => {
                const item = event.target.closest('.feedback-item');
                if (item) item.style.zIndex = '1000';
            };
            this._onDropdownHide = (event) => {
                const item = event.target.closest('.feedback-item');
                if (item) item.style.zIndex = '1';
            };
            this.element.addEventListener('show.bs.dropdown', this._onDropdownShow);
            this.element.addEventListener('hide.bs.dropdown', this._onDropdownHide);
        }

        disconnect() {
            this.element.removeEventListener('show.bs.dropdown', this._onDropdownShow);
            this.element.removeEventListener('hide.bs.dropdown', this._onDropdownHide);
        }

        // --- Filters (data-action on the filter selects + buttons) ---
        applyFilters() {
            const status = document.getElementById('statusFilter').value;
            const type = document.getElementById('typeFilter').value;
            const priority = document.getElementById('priorityFilter').value;

            const params = new URLSearchParams();
            if (status) params.append('status', status);
            if (type) params.append('type', type);
            if (priority) params.append('priority', priority);

            window.location.href = this.manageUrl + '?' + params.toString();
        }

        clearFilters() {
            window.location.href = this.manageUrl;
        }

        toggleMyFeedback() {
            const params = new URLSearchParams(window.location.search);
            if (params.get('mine') === '1') {
                params.delete('mine');
            } else {
                params.set('mine', '1');
            }
            window.location.href = this.manageUrl + '?' + params.toString();
        }

        // --- Status / priority (admin) ---
        updateStatus(event) {
            const id = event.currentTarget.dataset.feedbackId;
            const status = event.currentTarget.dataset.status;
            window.api(`/feedback/${id}/status`, { method: 'POST', body: { status: status }, notifyError: false })
                .then((data) => {
                    if (data && data.success) {
                        window.showSuccessMessage(data.message);
                        const badge = document.getElementById(`status-${id}`);
                        badge.textContent = status.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase());
                        badge.className = `badge ${data.feedback.status_badge_class} me-1`;
                    } else {
                        window.showErrorMessage('Error updating status: ' + ((data && data.message) || 'unknown'));
                    }
                })
                .catch((error) => {
                    window.showErrorMessage('Error updating status: ' + ((error && error.data && error.data.message) || error.message));
                });
        }

        updatePriority(event) {
            const id = event.currentTarget.dataset.feedbackId;
            const priority = event.currentTarget.dataset.priority;
            window.api(`/feedback/${id}/priority`, { method: 'POST', body: { priority: priority }, notifyError: false })
                .then((data) => {
                    if (data && data.success) {
                        window.showSuccessMessage(data.message);
                        const badge = document.getElementById(`priority-${id}`);
                        badge.textContent = priority.charAt(0).toUpperCase() + priority.slice(1);
                        badge.className = `badge ${data.feedback.priority_badge_class}`;
                    } else {
                        window.showErrorMessage('Error updating priority: ' + ((data && data.message) || 'unknown'));
                    }
                })
                .catch((error) => {
                    window.showErrorMessage('Error updating priority: ' + ((error && error.data && error.data.message) || error.message));
                });
        }

        // --- Edit (owner) ---
        editFeedback(event) {
            const ds = event.currentTarget.dataset;
            document.getElementById('editFeedbackId').value = ds.feedbackId;
            document.getElementById('editFeedbackTitle').value = ds.feedbackTitle || '';
            document.getElementById('editFeedbackDescription').value = ds.feedbackDescription || '';
            document.getElementById('editFeedbackType').value = ds.feedbackType;

            const modal = new bootstrap.Modal(document.getElementById('editFeedbackModal'));
            modal.show();
        }

        saveFeedback() {
            const id = document.getElementById('editFeedbackId').value;
            const data = {
                title: document.getElementById('editFeedbackTitle').value,
                description: document.getElementById('editFeedbackDescription').value,
                type: document.getElementById('editFeedbackType').value
            };
            window.api(`/feedback/${id}/update`, { method: 'POST', body: data, notifyError: false })
                .then((resp) => {
                    if (resp && resp.success) {
                        window.showSuccessMessage(resp.message);
                        location.reload();
                    } else {
                        window.showErrorMessage('Error updating feedback: ' + ((resp && resp.message) || 'unknown'));
                    }
                })
                .catch((error) => {
                    window.showErrorMessage('Error updating feedback: ' + ((error && error.data && error.data.message) || error.message));
                });
        }

        deleteFeedback(event) {
            if (!confirm('Are you sure you want to delete this feedback? This action cannot be undone.')) return;
            const id = event.currentTarget.dataset.feedbackId;
            const card = event.currentTarget.closest('.col-12');
            window.api(`/feedback/${id}/delete`, { method: 'DELETE', notifyError: false })
                .then((data) => {
                    if (data && data.success) {
                        window.showSuccessMessage(data.message);
                        if (card) card.remove();
                    } else {
                        window.showErrorMessage('Error deleting feedback: ' + ((data && data.message) || 'unknown'));
                    }
                })
                .catch((error) => {
                    window.showErrorMessage('Error deleting feedback: ' + ((error && error.data && error.data.message) || error.message));
                });
        }

        // Highlight the feedback card targeted by the URL hash (e.g. notification click).
        _highlightTargeted() {
            const hash = window.location.hash;
            if (!hash || hash.indexOf('#feedback-') !== 0) return;
            const wrapper = document.querySelector(hash);
            if (!wrapper) return;
            const card = wrapper.querySelector('.feedback-item');
            if (!card) return;
            card.classList.add('just-arrived');
            setTimeout(() => card.classList.remove('just-arrived'), 2500);
        }
}
